// Piezas que comparten las ventanas y tablas del tablero de SelleTrans.
import { X } from 'lucide-react';
import { avisoTope } from '@/lib/topeComerciante';

export const badgeCls = (status) => ({
  submitted:    'bg-blue-100 text-blue-700',
  under_review: 'bg-amber-100 text-amber-700',
  approved:     'bg-emerald-100 text-emerald-700',
  denied:       'bg-rose-100 text-rose-700',
  needs_info:   'bg-purple-100 text-purple-700',
  paid:         'bg-emerald-100 text-emerald-800',
}[status] || 'bg-slate-100 text-slate-600');

export const tierColor = (t) => ({
  A: 'from-emerald-500 to-green-500',
  B: 'from-blue-500 to-cyan-500',
  C: 'from-indigo-500 to-blue-500',
  D: 'from-purple-500 to-pink-500',
}[t] || 'from-amber-500 to-orange-500');

// Sin precio en los botones del tipo de cobertura (Mike, 4-oct: "should not
// show the price here"). Desde el 9-oct cada comerciante paga su propia tarifa,
// asi que el porcentaje del tier ya no era lo que se cobra: el monto real sale
// en el boton de emitir. Aca va solo el tipo de cobertura.
const TIPOS_DE_COBERTURA = {
  A: 'Loss & damage',
  B: 'Shared risk',
  C: '+ Chargeback',
  D: 'Full benefits',
};
export const tipoDeCobertura = (t) => (t.pricing_model === 'flat'
  ? `Up to $${Number(t.coverage_amount).toLocaleString()}`
  : TIPOS_DE_COBERTURA[t.code] || t.name);

export const INPUT = 'w-full h-11 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm';

export const StatCard = ({ label, value, icon: Icon, tone, testid }) => (
  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 flex items-center gap-3" data-testid={testid}>
    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${{
      purple: 'from-purple-500 to-indigo-500', emerald: 'from-emerald-500 to-green-500',
      blue: 'from-blue-500 to-cyan-500', amber: 'from-amber-500 to-orange-500'
    }[tone]} flex items-center justify-center`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold tabular-nums">{value}</p>
    </div>
  </div>
);

export const Row = ({ label, children }) => (
  <div>
    <label className="text-sm font-medium block mb-1.5">{label}</label>
    {children}
  </div>
);

export const Modal = ({ children, title, onClose, testid }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
    {/* Con alto maximo y desplazamiento: el formulario de cotizacion es mas
        alto que una pantalla de laptop y el boton quedaba fuera de alcance. */}
    <div className="bg-white dark:bg-slate-900 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

// El nombre de cada pais lo pone el navegador a partir del codigo ISO. Los
// nombres del documento de PACKIFI no se usan: vienen con guiones bajos y
// errores de tipeo ("Liechtenstien", "Papua_New_Grunea").
const nombrePais = (() => {
  let dn = null;
  try { dn = new Intl.DisplayNames(['en'], { type: 'region' }); } catch (e) { dn = null; }
  return (codigo) => {
    try { return (dn && dn.of(codigo)) || codigo; } catch (e) { return codigo; }
  };
})();

// Estados Unidos primero, despues el resto por nombre. Son los paises que el
// cuadro de PACKIFI cubre mas los que se cubren al maximo de la poliza.
export const paisesCotizables = (cfg) => {
  const codigos = new Set([
    ...(cfg.policy_max_countries || ['US']),
    ...Object.keys(cfg.country_designations || {}),
  ]);
  codigos.delete('US');
  const resto = [...codigos].map(c => [c, `${nombrePais(c)} (${c})`])
    .sort((a, b) => a[1].localeCompare(b[1]));
  return [['US', 'United States (US)'], ...resto];
};

// El aviso va ANTES de declarar el valor, no en el error. Montos y textos
// salen de la config: un numero copiado aca se desactualiza en silencio.
export const AvisoPais = ({ cfg, pais, carrier }) => {
  const prohibido = (cfg.uninsurable_countries || {})[pais];
  if (prohibido) {
    return <p className="text-xs text-red-600 mt-1">⚠ Shipments to or from {prohibido} are not covered.</p>;
  }
  if ((cfg.policy_max_countries || []).includes(pais)) return null;
  const avisos = [];
  if (/usps|postal/i.test(carrier || '')) avisos.push('International USPS shipments are not covered.');
  const desig = (cfg.country_designations || {})[pais];
  if (desig) {
    const tope = (cfg.designation_max || {})[desig];
    const hasta = (cfg.designation_covered_to || {})[desig];
    avisos.push(`Risk designation ${desig}: capped at $${Number(tope).toLocaleString()}, covered to the ${hasta}.`);
  }
  if (cfg.international_customs_days) {
    avisos.push(`Coverage ends on delivery or ${cfg.international_customs_days} days after arrival while awaiting customs, whichever comes first.`);
  }
  return avisos.length
    ? <p className="text-xs text-amber-600 mt-1">⚠ {avisos.join(' ')}</p>
    : null;
};

export const categoriaElegida = (cfg, codigo) => (cfg?.commodity_categories || []).find(c => c.code === codigo);

// Lo mismo que decide el backend (mercaderia.py), dicho antes de enviar: el
// comerciante tiene que saber que hay que llamar ANTES de recibir el rechazo.
export const AvisoMercaderia = ({ cfg, setup, categoria, subtipo, fragil, pais, codigo }) => {
  const cat = categoriaElegida(cfg, categoria);
  if (!cat) return null;
  const sub = (cat.subtypes || []).find(s => s.code === subtipo);
  const telefono = cfg.office_phone;
  const avisos = [];
  if (cat.needs_approval || sub?.needs_approval) {
    avisos.push(`${sub ? sub.label : cat.label} are covered case by case: call the office at ${telefono} for an approval code.`);
  }
  if (fragil) {
    const tope = pais === 'US' ? cfg.fragile_max_domestic : cfg.fragile_max_international;
    avisos.push(`Fragile items are capped at $${Number(tope).toLocaleString()}${pais === 'US' ? ' in the U.S.' : ' internationally'}. Above that, call the office at ${telefono}.`);
  }
  if (fragil) {
    // Mike, 6-oct: solo perdida total, y dos fotos antes de despachar.
    if (cfg.fragile_condition) avisos.push(cfg.fragile_condition);
    if (cfg.fragile_photo_condition) avisos.push(cfg.fragile_photo_condition);
  }
  const tope = avisoTope(cat, setup, telefono);
  if (tope) avisos.push(tope);
  if (!avisos.length) return null;
  return (
    <p className={`text-xs mt-1 ${codigo ? 'text-slate-500' : 'text-amber-600'}`} data-testid="q-goods-warning">
      ⚠ {avisos.join(' ')}{codigo ? ' Your approval code will be checked when you submit.' : ''}
    </p>
  );
};

// Sube una foto (multipart). fetchJson manda JSON, por eso va aparte.
export async function subirFoto(ruta, token, archivo, serial) {
  const fd = new FormData();
  fd.append('image', archivo);
  if (serial && serial.trim()) fd.append('serial', serial.trim());
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${ruta}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(typeof d.detail === 'string' ? d.detail : (d.detail?.message || `Upload failed (${r.status})`));
    e.status = r.status;
    throw e;
  }
  return d;
}
